import React, { useRef } from 'react';
import { Download, Share2 } from 'lucide-react';

const ShareImageGenerator = () => {
  const canvasRef = useRef(null);

  const generateImage = (aspectRatio = '16:9') => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    
    // Set dimensions based on aspect ratio
    const dimensions = {
      '16:9': { width: 1920, height: 1080, name: 'landscape' },
      '1:1': { width: 1200, height: 1200, name: 'square' },
      '9:16': { width: 1080, height: 1920, name: 'vertical' }
    };

    const { width, height, name } = dimensions[aspectRatio];
    canvas.width = width;
    canvas.height = height;

    // Background gradient
    const gradient = ctx.createLinearGradient(0, 0, width, height);
    gradient.addColorStop(0, '#1e3a8a');
    gradient.addColorStop(0.5, '#7c2d92');
    gradient.addColorStop(1, '#991b1b');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    if (aspectRatio === '16:9') {
      // ===== 16:9 LANDSCAPE LAYOUT =====
      
      // Title
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 80px Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('The Acceleration Nobody Saw Coming', width / 2, 90);

      // Subtitle
      ctx.font = '40px Arial, sans-serif';
      ctx.fillStyle = '#93c5fd';
      ctx.fillText('McKinsey Automation Assessments', width / 2, 145);

      // Main comparison boxes
      const boxY = 210;
      const boxWidth = 420;
      const boxHeight = 260;
      const boxSpacing = 140;
      const leftBoxX = (width - (boxWidth * 2 + boxSpacing)) / 2;
      const rightBoxX = leftBoxX + boxWidth + boxSpacing;

      // Left box (2023)
      ctx.fillStyle = 'rgba(251, 191, 36, 0.25)';
      ctx.fillRect(leftBoxX, boxY, boxWidth, boxHeight);
      ctx.strokeStyle = '#fbbf24';
      ctx.lineWidth = 6;
      ctx.strokeRect(leftBoxX, boxY, boxWidth, boxHeight);

      ctx.fillStyle = '#fef3c7';
      ctx.font = 'bold 42px Arial, sans-serif';
      ctx.fillText('McKinsey 2023', leftBoxX + boxWidth / 2, boxY + 65);

      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 140px Arial, sans-serif';
      ctx.fillText('30%', leftBoxX + boxWidth / 2, boxY + 175);

      ctx.font = '32px Arial, sans-serif';
      ctx.fillStyle = '#fef3c7';
      ctx.fillText('by 2030', leftBoxX + boxWidth / 2, boxY + 225);

      // Arrow
      ctx.strokeStyle = '#fbbf24';
      ctx.lineWidth = 12;
      ctx.beginPath();
      ctx.moveTo(leftBoxX + boxWidth + 25, boxY + boxHeight / 2);
      ctx.lineTo(rightBoxX - 25, boxY + boxHeight / 2);
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(rightBoxX - 25, boxY + boxHeight / 2);
      ctx.lineTo(rightBoxX - 70, boxY + boxHeight / 2 - 35);
      ctx.lineTo(rightBoxX - 70, boxY + boxHeight / 2 + 35);
      ctx.closePath();
      ctx.fillStyle = '#fbbf24';
      ctx.fill();

      // Right box (2025)
      ctx.fillStyle = 'rgba(239, 68, 68, 0.25)';
      ctx.fillRect(rightBoxX, boxY, boxWidth, boxHeight);
      ctx.strokeStyle = '#f87171';
      ctx.lineWidth = 6;
      ctx.strokeRect(rightBoxX, boxY, boxWidth, boxHeight);

      ctx.fillStyle = '#fecaca';
      ctx.font = 'bold 42px Arial, sans-serif';
      ctx.fillText('McKinsey 2025', rightBoxX + boxWidth / 2, boxY + 65);

      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 140px Arial, sans-serif';
      ctx.fillText('57%', rightBoxX + boxWidth / 2, boxY + 175);

      ctx.font = '32px Arial, sans-serif';
      ctx.fillStyle = '#fecaca';
      ctx.fillText('NOW', rightBoxX + boxWidth / 2, boxY + 225);

      // Change indicator
      ctx.fillStyle = '#ef4444';
      ctx.font = 'bold 58px Arial, sans-serif';
      ctx.fillText('+90% in just 2 years', width / 2, 550);

      // What Changed section
      ctx.fillStyle = '#93c5fd';
      ctx.font = 'bold 36px Arial, sans-serif';
      ctx.fillText('What Changed:', width / 2, 630);

      // Timeline cards (horizontal)
      const cardWidth = 450;
      const cardHeight = 120;
      const cardGap = 40;
      const totalCardsWidth = (cardWidth * 3) + (cardGap * 2);
      let cardX = (width - totalCardsWidth) / 2;
      const cardY = 680;

      const changes = [
        { year: '2017', text: '50% theoretical potential', color: '#3b82f6' },
        { year: '2023', text: '30% by 2030 (conservative)', color: '#eab308' },
        { year: '2025', text: '57% NOW (AI agents + robots)', color: '#ef4444' }
      ];

      changes.forEach((change) => {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.15)';
        ctx.fillRect(cardX, cardY, cardWidth, cardHeight);
        
        ctx.fillStyle = change.color;
        ctx.font = 'bold 36px Arial, sans-serif';
        ctx.fillText(change.year, cardX + cardWidth / 2, cardY + 45);
        
        ctx.fillStyle = '#ffffff';
        ctx.font = '26px Arial, sans-serif';
        ctx.fillText(change.text, cardX + cardWidth / 2, cardY + 85);
        
        cardX += cardWidth + cardGap;
      });

      // Footer
      ctx.fillStyle = '#93c5fd';
      ctx.font = 'bold 42px Arial, sans-serif';
      ctx.fillText('PivotIntel.org/app/timeline', width / 2, 950);

      ctx.font = '28px Arial, sans-serif';
      ctx.fillStyle = '#bfdbfe';
      ctx.fillText('Weekly intelligence on AI automation risk', width / 2, 995);

    } else if (aspectRatio === '1:1') {
      // ===== 1:1 SQUARE LAYOUT =====
      
      // Title
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 64px Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('The Acceleration Nobody Saw Coming', width / 2, 80);

      // Subtitle
      ctx.font = '32px Arial, sans-serif';
      ctx.fillStyle = '#93c5fd';
      ctx.fillText('McKinsey Automation Assessments', width / 2, 130);

      // Main comparison boxes
      const boxY = 190;
      const boxWidth = 400;
      const boxHeight = 240;
      const boxSpacing = 100;
      const leftBoxX = (width - (boxWidth * 2 + boxSpacing)) / 2;
      const rightBoxX = leftBoxX + boxWidth + boxSpacing;

      // Left box (2023)
      ctx.fillStyle = 'rgba(251, 191, 36, 0.25)';
      ctx.fillRect(leftBoxX, boxY, boxWidth, boxHeight);
      ctx.strokeStyle = '#fbbf24';
      ctx.lineWidth = 5;
      ctx.strokeRect(leftBoxX, boxY, boxWidth, boxHeight);

      ctx.fillStyle = '#fef3c7';
      ctx.font = 'bold 36px Arial, sans-serif';
      ctx.fillText('McKinsey 2023', leftBoxX + boxWidth / 2, boxY + 55);

      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 110px Arial, sans-serif';
      ctx.fillText('30%', leftBoxX + boxWidth / 2, boxY + 150);

      ctx.font = '28px Arial, sans-serif';
      ctx.fillStyle = '#fef3c7';
      ctx.fillText('by 2030', leftBoxX + boxWidth / 2, boxY + 195);

      // Arrow
      ctx.strokeStyle = '#fbbf24';
      ctx.lineWidth = 10;
      ctx.beginPath();
      ctx.moveTo(leftBoxX + boxWidth + 15, boxY + boxHeight / 2);
      ctx.lineTo(rightBoxX - 15, boxY + boxHeight / 2);
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(rightBoxX - 15, boxY + boxHeight / 2);
      ctx.lineTo(rightBoxX - 50, boxY + boxHeight / 2 - 28);
      ctx.lineTo(rightBoxX - 50, boxY + boxHeight / 2 + 28);
      ctx.closePath();
      ctx.fillStyle = '#fbbf24';
      ctx.fill();

      // Right box (2025)
      ctx.fillStyle = 'rgba(239, 68, 68, 0.25)';
      ctx.fillRect(rightBoxX, boxY, boxWidth, boxHeight);
      ctx.strokeStyle = '#f87171';
      ctx.lineWidth = 5;
      ctx.strokeRect(rightBoxX, boxY, boxWidth, boxHeight);

      ctx.fillStyle = '#fecaca';
      ctx.font = 'bold 36px Arial, sans-serif';
      ctx.fillText('McKinsey 2025', rightBoxX + boxWidth / 2, boxY + 55);

      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 110px Arial, sans-serif';
      ctx.fillText('57%', rightBoxX + boxWidth / 2, boxY + 150);

      ctx.font = '28px Arial, sans-serif';
      ctx.fillStyle = '#fecaca';
      ctx.fillText('NOW', rightBoxX + boxWidth / 2, boxY + 195);

      // Change indicator
      ctx.fillStyle = '#ef4444';
      ctx.font = 'bold 48px Arial, sans-serif';
      ctx.fillText('+90% in just 2 years', width / 2, 510);

      // What Changed section
      ctx.fillStyle = '#93c5fd';
      ctx.font = 'bold 32px Arial, sans-serif';
      ctx.fillText('What Changed:', width / 2, 590);

      // Timeline cards (horizontal)
      const cardWidth = 360;
      const cardHeight = 110;
      const cardGap = 30;
      const totalCardsWidth = (cardWidth * 3) + (cardGap * 2);
      let cardX = (width - totalCardsWidth) / 2;
      const cardY = 640;

      const changes = [
        { year: '2017', text: '50% theoretical potential', color: '#3b82f6' },
        { year: '2023', text: '30% by 2030 (conservative)', color: '#eab308' },
        { year: '2025', text: '57% NOW (AI agents + robots)', color: '#ef4444' }
      ];

      changes.forEach((change) => {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.15)';
        ctx.fillRect(cardX, cardY, cardWidth, cardHeight);
        
        ctx.fillStyle = change.color;
        ctx.font = 'bold 32px Arial, sans-serif';
        ctx.fillText(change.year, cardX + cardWidth / 2, cardY + 40);
        
        ctx.fillStyle = '#ffffff';
        ctx.font = '24px Arial, sans-serif';
        ctx.fillText(change.text, cardX + cardWidth / 2, cardY + 75);
        
        cardX += cardWidth + cardGap;
      });

      // Footer
      ctx.fillStyle = '#93c5fd';
      ctx.font = 'bold 38px Arial, sans-serif';
      ctx.fillText('PivotIntel.org/app/timeline', width / 2, 1050);

      ctx.font = '26px Arial, sans-serif';
      ctx.fillStyle = '#bfdbfe';
      ctx.fillText('Weekly intelligence on AI automation risk', width / 2, 1090);

    } else {
      // ===== 9:16 VERTICAL LAYOUT =====
      
      // Title
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 52px Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('The Acceleration', width / 2, 100);
      ctx.fillText('Nobody Saw Coming', width / 2, 160);

      // Subtitle
      ctx.font = '28px Arial, sans-serif';
      ctx.fillStyle = '#93c5fd';
      ctx.fillText('McKinsey Automation Assessments', width / 2, 210);

      // Boxes (vertical stack)
      const boxWidth = 700;
      const boxHeight = 240;
      const boxX = (width - boxWidth) / 2;
      let boxY = 270;

      // Top box (2023)
      ctx.fillStyle = 'rgba(251, 191, 36, 0.25)';
      ctx.fillRect(boxX, boxY, boxWidth, boxHeight);
      ctx.strokeStyle = '#fbbf24';
      ctx.lineWidth = 5;
      ctx.strokeRect(boxX, boxY, boxWidth, boxHeight);

      ctx.fillStyle = '#fef3c7';
      ctx.font = 'bold 38px Arial, sans-serif';
      ctx.fillText('McKinsey 2023', width / 2, boxY + 55);

      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 120px Arial, sans-serif';
      ctx.fillText('30%', width / 2, boxY + 150);

      ctx.font = '30px Arial, sans-serif';
      ctx.fillStyle = '#fef3c7';
      ctx.fillText('by 2030', width / 2, boxY + 200);

      // Down arrow
      const arrowStartY = boxY + boxHeight + 30;
      const arrowEndY = arrowStartY + 100;
      
      ctx.strokeStyle = '#fbbf24';
      ctx.lineWidth = 12;
      ctx.beginPath();
      ctx.moveTo(width / 2, arrowStartY);
      ctx.lineTo(width / 2, arrowEndY);
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(width / 2, arrowEndY);
      ctx.lineTo(width / 2 - 35, arrowEndY - 55);
      ctx.lineTo(width / 2 + 35, arrowEndY - 55);
      ctx.closePath();
      ctx.fillStyle = '#fbbf24';
      ctx.fill();

      boxY = arrowEndY + 30;

      // Bottom box (2025)
      ctx.fillStyle = 'rgba(239, 68, 68, 0.25)';
      ctx.fillRect(boxX, boxY, boxWidth, boxHeight);
      ctx.strokeStyle = '#f87171';
      ctx.lineWidth = 5;
      ctx.strokeRect(boxX, boxY, boxWidth, boxHeight);

      ctx.fillStyle = '#fecaca';
      ctx.font = 'bold 38px Arial, sans-serif';
      ctx.fillText('McKinsey 2025', width / 2, boxY + 55);

      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 120px Arial, sans-serif';
      ctx.fillText('57%', width / 2, boxY + 150);

      ctx.font = '30px Arial, sans-serif';
      ctx.fillStyle = '#fecaca';
      ctx.fillText('NOW', width / 2, boxY + 200);

      // Change indicator
      ctx.fillStyle = '#ef4444';
      ctx.font = 'bold 48px Arial, sans-serif';
      ctx.fillText('+90% in just 2 years', width / 2, boxY + boxHeight + 80);

      // What Changed section
      ctx.fillStyle = '#93c5fd';
      ctx.font = 'bold 32px Arial, sans-serif';
      ctx.fillText('What Changed:', width / 2, boxY + boxHeight + 150);

      // Timeline cards (vertical stack)
      const cardWidth = 700;
      const cardHeight = 100;
      const cardGap = 20;
      const cardX = (width - cardWidth) / 2;
      let cardY = boxY + boxHeight + 200;

      const changes = [
        { year: '2017', text: '50% theoretical potential', color: '#3b82f6' },
        { year: '2023', text: '30% by 2030 (conservative)', color: '#eab308' },
        { year: '2025', text: '57% NOW (AI agents + robots)', color: '#ef4444' }
      ];

      changes.forEach((change) => {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.15)';
        ctx.fillRect(cardX, cardY, cardWidth, cardHeight);
        
        ctx.fillStyle = change.color;
        ctx.font = 'bold 32px Arial, sans-serif';
        ctx.fillText(change.year, width / 2, cardY + 38);
        
        ctx.fillStyle = '#ffffff';
        ctx.font = '24px Arial, sans-serif';
        ctx.fillText(change.text, width / 2, cardY + 72);
        
        cardY += cardHeight + cardGap;
      });

      // Footer
      ctx.fillStyle = '#93c5fd';
      ctx.font = 'bold 38px Arial, sans-serif';
      ctx.fillText('PivotIntel.org/app/timeline', width / 2, 1740);

      ctx.font = '26px Arial, sans-serif';
      ctx.fillStyle = '#bfdbfe';
      ctx.fillText('Weekly intelligence on AI automation risk', width / 2, 1785);
    }

    // Trigger download
    const link = document.createElement('a');
    link.download = `mckinsey-timeline-${name}-${aspectRatio.replace(':', 'x')}.png`;
    link.href = canvas.toDataURL('image/png', 1.0);
    link.click();
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 border-2 border-gray-200">
      <div className="flex items-center gap-3 mb-6">
        <Share2 className="h-6 w-6 text-blue-600" />
        <h3 className="text-2xl font-bold">Share This Timeline</h3>
      </div>

      <p className="text-gray-600 mb-6">
        Download high-quality graphics in your preferred aspect ratio. Perfect for social media, presentations, or reports.
      </p>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <button
          onClick={() => generateImage('16:9')}
          className="flex flex-col items-center gap-2 bg-blue-600 text-white px-6 py-4 rounded-lg hover:bg-blue-700 font-semibold transition-all"
        >
          <Download className="h-5 w-5" />
          <span>16:9 Landscape</span>
          <span className="text-xs opacity-80">1920×1080</span>
        </button>

        <button
          onClick={() => generateImage('1:1')}
          className="flex flex-col items-center gap-2 bg-purple-600 text-white px-6 py-4 rounded-lg hover:bg-purple-700 font-semibold transition-all"
        >
          <Download className="h-5 w-5" />
          <span>1:1 Square</span>
          <span className="text-xs opacity-80">1200×1200</span>
        </button>

        <button
          onClick={() => generateImage('9:16')}
          className="flex flex-col items-center gap-2 bg-orange-600 text-white px-6 py-4 rounded-lg hover:bg-orange-700 font-semibold transition-all"
        >
          <Download className="h-5 w-5" />
          <span>9:16 Vertical</span>
          <span className="text-xs opacity-80">1080×1920</span>
        </button>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-900">
          <strong>What's included:</strong> Header, 30% → 57% comparison, +90% change indicator, 
          3 timeline cards showing progression (2017/2023/2025), and PivotIntel branding. High-resolution PNG format.
        </p>
      </div>

      {/* Hidden canvas for image generation */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </div>
  );
};

export default ShareImageGenerator;
